import React, { useState } from 'react';
import { frequencyOptions, daysOfWeek, paymentTermsOptions } from '../utils/sampleData';
import { DEFAULT_NOTIFICATION_PREFS } from '../utils/notificationsApi';

function ClientForm({ client, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    firstName: client?.firstName || '',
    lastName: client?.lastName || '',
    email: client?.email || '',
    phone: client?.phone || '',
    clientType: client?.clientType || 'residential',
    status: client?.status || 'active',
    frequency: client?.frequency || '',
    preferredDay: client?.preferredDay || '',
    textConsent: client?.textConsent ?? true,
    notes: client?.notes || '',
    // Billing settings
    autoInvoice: client?.autoInvoice ?? true,
    depositExempt: client?.depositExempt ?? false,
    paymentTerms: client?.paymentTerms || 'due_on_receipt',
    accountingEmail: client?.accountingEmail || '',
    // Notification preferences (granular)
    notificationPrefs: client?.notificationPrefs || { ...DEFAULT_NOTIFICATION_PREFS }
  });

  const [errors, setErrors] = useState({});
  const [showNotificationDetails, setShowNotificationDetails] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'textConsent' || name === 'autoInvoice' || name === 'depositExempt') {
        setFormData(prev => ({ ...prev, [name]: checked }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Handle notification preference changes
  const handleNotificationPrefChange = (category, channel) => {
    setFormData(prev => ({
      ...prev,
      notificationPrefs: {
        ...prev.notificationPrefs,
        [category]: {
          ...prev.notificationPrefs[category],
          [channel]: !prev.notificationPrefs[category]?.[channel]
        }
      }
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.accountingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.accountingEmail)) {
      newErrors.accountingEmail = 'Invalid email format';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  // Notification category labels
  const notificationCategories = [
    { key: 'appointment', label: 'Appointment Reminders', description: 'Confirmation and reminders before cleaning' },
    { key: 'teamStatus', label: 'Team Updates', description: 'On the way, arrived, and completed notifications' },
    { key: 'invoices', label: 'Invoice & Payments', description: 'Invoice sent, payment confirmations, and reminders' },
    { key: 'estimates', label: 'Estimates', description: 'When new estimates are ready for review' },
    { key: 'portalMessages', label: 'Portal Messages', description: 'When there\'s a new message in their portal' }
  ];

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">
            First Name <span className="required">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            className="form-input"
            value={formData.firstName}
            onChange={handleChange}
            placeholder="Enter first name"
          />
          {errors.firstName && <span className="form-error">{errors.firstName}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Last Name <span className="required">*</span>
          </label>
          <input
            type="text"
            name="lastName"
            className="form-input"
            value={formData.lastName}
            onChange={handleChange}
            placeholder="Enter last name"
          />
          {errors.lastName && <span className="form-error">{errors.lastName}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">
            Phone <span className="required">*</span>
          </label>
          <input
            type="tel"
            name="phone"
            className="form-input"
            value={formData.phone}
            onChange={handleChange}
            placeholder="(434) 555-0000"
          />
          {errors.phone && <span className="form-error">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            className="form-input"
            value={formData.email}
            onChange={handleChange}
            placeholder="email@example.com"
          />
          {errors.email && <span className="form-error">{errors.email}</span>}
        </div>

        <div className="form-section-title">Client Details</div>

        <div className="form-group">
          <label className="form-label">Client Type</label>
          <select
            name="clientType"
            className="form-select"
            value={formData.clientType}
            onChange={handleChange}
          >
            <option value="residential">Residential</option>
            <option value="property_manager">Property Manager</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Status</label>
          <select
            name="status"
            className="form-select"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="lead">Lead</option>
          </select>
        </div>

        <div className="form-section-title">Schedule Preferences</div>

        <div className="form-group">
          <label className="form-label">Frequency</label>
          <select
            name="frequency"
            className="form-select"
            value={formData.frequency}
            onChange={handleChange}
          >
            <option value="">Select frequency</option>
            {frequencyOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Preferred Day</label>
          <select
            name="preferredDay"
            className="form-select"
            value={formData.preferredDay}
            onChange={handleChange}
          >
            <option value="">No preference</option>
            {daysOfWeek.map(day => (
              <option key={day} value={day}>{day}</option>
            ))}
          </select>
        </div>

        <div className="form-section-title">Communication Preferences</div>

        <div className="form-group full-width">
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              name="textConsent"
              checked={formData.textConsent}
              onChange={handleChange}
            />
            Client has consented to receive text messages
          </label>
          <span className="form-hint">Required for SMS notifications</span>
        </div>

        {/* Notification Preferences Summary */}
        <div className="form-group full-width">
          <div className="notification-prefs-header">
            <label className="form-label">Notification Preferences</label>
            <button 
              type="button" 
              className="btn-link-small"
              onClick={() => setShowNotificationDetails(!showNotificationDetails)}
            >
              {showNotificationDetails ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
          
          {showNotificationDetails ? (
            <div className="notification-prefs-grid">
              <div className="notification-prefs-header-row">
                <span></span>
                <span className="channel-label">Email</span>
                <span className="channel-label">Text</span>
              </div>
              {notificationCategories.map(cat => (
                <div key={cat.key} className="notification-pref-row">
                  <div className="notification-pref-info">
                    <span className="notification-pref-label">{cat.label}</span>
                    <span className="notification-pref-desc">{cat.description}</span>
                  </div>
                  <label className="notification-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notificationPrefs[cat.key]?.email || false}
                      onChange={() => handleNotificationPrefChange(cat.key, 'email')}
                      disabled={!formData.email}
                    />
                  </label>
                  <label className="notification-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.notificationPrefs[cat.key]?.sms || false}
                      onChange={() => handleNotificationPrefChange(cat.key, 'sms')}
                      disabled={!formData.phone || !formData.textConsent}
                    />
                  </label>
                </div>
              ))}
            </div>
          ) : (
            <div className="notification-prefs-summary">
              <span>
                Email: {Object.values(formData.notificationPrefs).filter(p => p.email).length} categories
              </span>
              <span>
                Text: {Object.values(formData.notificationPrefs).filter(p => p.sms).length} categories
              </span>
            </div>
          )}
        </div>

        <div className="form-section-title">Billing Settings</div>

        <div className="form-group">
          <label className="form-label">Payment Terms</label>
          <select
            name="paymentTerms"
            className="form-select"
            value={formData.paymentTerms}
            onChange={handleChange}
          >
            {paymentTermsOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Accounting Email</label>
          <input
            type="email"
            name="accountingEmail"
            className="form-input"
            value={formData.accountingEmail}
            onChange={handleChange}
            placeholder="accounting@example.com"
          />
          {errors.accountingEmail && <span className="form-error">{errors.accountingEmail}</span>}
          <span className="form-hint">If set, invoices will be sent to this email instead of the client's main email</span>
        </div>

        <div className="form-group">
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              name="autoInvoice"
              checked={formData.autoInvoice}
              onChange={handleChange}
            />
            Draft invoice upon job completion
          </label>
          <span className="form-hint">Automatically create a draft invoice when jobs for this client are completed</span>
        </div>

        <div className="form-group">
          <label className="form-checkbox-label">
            <input
              type="checkbox"
              name="depositExempt"
              checked={formData.depositExempt}
              onChange={handleChange}
            />
            Exempt from prepayment requirement
          </label>
          <span className="form-hint">Skip the full prepayment requirement for this client's first clean. An invoice will still be generated.</span>
        </div>

        <div className="form-group full-width">
          <label className="form-label">Notes</label>
          <textarea
            name="notes"
            className="form-textarea"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Any additional notes about this client..."
            rows={3}
          />
        </div>
      </div>

      <div className="form-actions">
        <button type="button" className="btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn-save">
          {client ? 'Save Changes' : 'Add Client'}
        </button>
      </div>
    </form>
  );
}

export default ClientForm;
